import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faArrowRightFromBracket,
} from '@fortawesome/pro-light-svg-icons';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import { sendSlackMessage, SlackMessagePayload } from '../../auth/ui/utils';
import { signOut } from '../../auth';
import { getUserData, getSessionGhostedEmail } from '../../auth/context';
import TrueImpactButton from '../../design-system/components/TrueImpactButton';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import TrueImpactTextfield from '../../design-system/components/TrueImpactTextfield';

interface TrueImpactErrorScreenProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  showReload?: boolean;
  showHome?: boolean;
  showLogout?: boolean;
  showNotify?: boolean;
  error?: unknown;
  context?: string;
}

const formatErrorStack = (error: unknown): string => {
  const err = error as { message?: string; stack?: string } | undefined;
  if (!err || !err.stack) {
    return `*Error:* ${(err && err.message) ||
      'No error message available'}\n\n*Stack Trace:* No stack trace available.`;
  }
  const formattedStack = err.stack
    .split('\n')
    .map(line => line.trim())
    .join('\n');
  return `*Error:* ${err.message}\n\n*Stack Trace:*\n${formattedStack}`;
};

const buildSlackPayload = (
  error: unknown,
  userMessage: string,
  context?: string,
): SlackMessagePayload => {
  const url = typeof window !== 'undefined' ? window.location.href : 'unknown';
  const userAgent =
    typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';

  let userInfoText = '*User:* unknown (unauthenticated or unavailable)';
  try {
    const userData = getUserData();
    const ghostEmail = getSessionGhostedEmail();
    if (userData) {
      const name =
        `${userData.givenName || ''} ${userData.familyName || ''}`.trim() ||
        userData.username ||
        'N/A';
      const parts = [
        `*Name:* ${name}`,
        `*Email:* ${userData.email || 'N/A'}`,
        `*User ID:* ${userData.userId || 'N/A'}`,
        `*Account ID:* ${userData.accountId || 'N/A'}`,
        `*Type:* ${userData.type || 'N/A'}`,
      ];
      if (ghostEmail) {
        parts.push(`*Ghosting as:* ${ghostEmail}`);
      }
      userInfoText = parts.join('\n');
    }
  } catch (e) {
    // best-effort; keep the fallback text
  }

  return {
    payload: {
      text: 'Something went wrong',
      blocks: [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Context:* ${context ||
              'TrueImpactErrorScreen'}\n*URL:* <${url}|${url}>\n*User Agent:* ${userAgent}\n*Message from user:* ${userMessage ||
              'No message provided.'}`,
          },
        },
        { type: 'divider' },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: userInfoText,
          },
        },
        { type: 'divider' },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: formatErrorStack(error),
          },
        },
      ],
    },
  };
};

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  background: ${COLORS.whiteBase};
  padding: ${SPACERS.large};
  z-index: 9999;
`;

const Card = styled.div`
  max-width: 600px;
  width: 100%;
  height: fit-content;
  margin: ${SPACERS.large} auto;
  background: ${COLORS.whiteBase};
  border-radius: 12px;
  border: 1px solid ${COLORS.greyLight};
  box-shadow: 0 12px 40px rgba(17, 24, 39, 0.06);
  padding: ${SPACERS.extraLarge};
  text-align: center;

  textarea {
    min-height: 120px;
  }

  .refresh-button,
  .notify-button {
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.small};
    margin-left: auto;
    margin-right: auto;
  }
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${COLORS.criticalBackground};
  color: ${COLORS.critical};
  margin: 0 auto ${SPACERS.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
`;

const FooterActions = styled.div`
  margin-top: ${SPACERS.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACERS.small};
`;

const FooterDivider = styled.span`
  width: 1px;
  height: 14px;
  background: ${COLORS.greyLight};
  display: inline-block;
`;

const FooterLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  color: ${COLORS.ctaPrimary};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  svg {
    font-size: 13px;
    color: currentColor;
  }

  &:hover {
    color: ${COLORS.ctaPrimaryHover};

    .footer-link-label {
      text-decoration: underline;
    }
  }
`;

type NotifyStatus = 'idle' | 'sending' | 'success' | 'error';

const TrueImpactErrorScreen: React.FC<TrueImpactErrorScreenProps> = ({
  title = 'It\'s not you, it\'s us.',
  message = 'Something went wrong on our end. Please try refreshing the page.',
  onRetry,
  showReload = true,
  showHome = true,
  showLogout = true,
  showNotify = true,
  error,
  context,
}) => {
  const [notifyStatus, setNotifyStatus] = useState<NotifyStatus>('idle');
  const [notifyError, setNotifyError] = useState<string>('');
  const userMessageRef = useRef<string>('');

  const handleReload = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  };

  const handleHome = () => {
    window.location.href = '/';
  };

  const handleLogout = async () => {
    try {
      await signOut();
      window.location.reload();
    } catch (e) {
      window.location.href = '/';
    }
  };

  const handleNotify = async () => {
    setNotifyStatus('sending');
    setNotifyError('');
    try {
      const res = await sendSlackMessage(
        buildSlackPayload(error, userMessageRef.current, context),
      );
      if (res && res.success) {
        setNotifyStatus('success');
      } else {
        setNotifyStatus('error');
        setNotifyError(
          (res && res.error && res.error.message) ||
            'Something went wrong while sending the request',
        );
      }
    } catch (e) {
      setNotifyStatus('error');
      setNotifyError('Something went wrong while sending the request');
    }
  };

  const notifySucceeded = notifyStatus === 'success';

  return (
    <Wrapper role="alert" aria-live="assertive">
      <Card>
        <IconCircle aria-hidden>!</IconCircle>
        <TrueImpactText variant="heading">{title}</TrueImpactText>
        <TrueImpactText>{message}</TrueImpactText>

        {showReload && (
          <TrueImpactButton
            variant="secondary-large"
            onClick={handleReload}
            className="refresh-button"
          >
            Refresh now
          </TrueImpactButton>
        )}

        {showNotify && !notifySucceeded && (
          <>
            <TrueImpactText>
              If the error persists, please notify us by clicking the button
              below.
            </TrueImpactText>
            <TrueImpactTextfield
              id="ti-error-notify-message"
              isTextarea
              placeholder="Message (optional)"
              onChange={value => {
                userMessageRef.current = value;
              }}
            />
            {notifyStatus === 'error' && (
              <TrueImpactText color="critical">{notifyError}</TrueImpactText>
            )}
            <TrueImpactButton
              onClick={handleNotify}
              isLoading={notifyStatus === 'sending'}
              className="notify-button"
            >
              Notify True Impact
            </TrueImpactButton>
          </>
        )}

        {notifySucceeded && (
          <TrueImpactText color="success">
            Successfully notified True Impact. You can refresh your page now.
          </TrueImpactText>
        )}

        {(showHome || showLogout) && (
          <FooterActions>
            {showHome && (
              <FooterLink onClick={handleHome}>
                <FontAwesomeIcon icon={faHouse} aria-hidden />
                <span className="footer-link-label">Go to home</span>
              </FooterLink>
            )}
            {showHome && showLogout && <FooterDivider aria-hidden />}
            {showLogout && (
              <FooterLink onClick={handleLogout}>
                <FontAwesomeIcon icon={faArrowRightFromBracket} aria-hidden />
                <span className="footer-link-label">Log out</span>
              </FooterLink>
            )}
          </FooterActions>
        )}
      </Card>
    </Wrapper>
  );
};

export default TrueImpactErrorScreen;
